<template>
  <td :colspan="colspan">
    <div class="d-flex justify-end mb-2 mt-2">
      <v-btn
        v-if="isEditing"
        icon
        small
        color="success"
        class="mr-1"
        @click="$emit('add-row')"
      >
        <v-icon small>mdi-plus</v-icon>
      </v-btn>
      <v-btn
        v-if="!isEditing"
        icon
        small
        color="primary"
        @click="$emit('start-edit')"
      >
        <v-icon small>mdi-pencil</v-icon>
      </v-btn>
      <template v-else>
        <v-btn
          icon
          small
          color="success"
          class="mr-1"
          @click="$emit('save-edit')"
          :loading="isSaving"
        >
          <v-icon small>mdi-content-save</v-icon>
        </v-btn>
        <v-btn icon small color="error" @click="$emit('cancel-edit')">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </div>
    <table class="my-2 rows-table">
      <thead>
        <tr>
          <th class="item-row-number-header">
            #
          </th>
          <th class="item-name-header">
            {{ $t("FORM_LABELS.ITEM_NAME") }}
          </th>
          <th>{{ $t("FORM_LABELS.ACCOUNTING_VALUE_TYPE") }}</th>
          <th>{{ $t("FORM_LABELS.ACCOUNTING_VALUE") }}</th>
          <th>{{ $t("FORM_LABELS.QUANTITY") }}</th>
          <th>{{ $t("FORM_LABELS.PRICE") }}</th>
          <th>{{ $t("FORM_LABELS.DISCOUNT") }}</th>
          <th>{{ $t("FORM_LABELS.TOTAL_PRICE") }}</th>
          <th v-if="isEditing" style="width: 60px;">
            {{ $t("BUTTON.ACTIONS") }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, index) in rows"
          :key="row.id || `new-${index}`"
          :class="{ 'error-row': isErrorRow(row) }"
        >
          <td class="pr-4 item-row-number-cell">
            <v-text-field
              v-if="isEditing"
              :value="row.itemRowNumber"
              type="number"
              dense
              hide-details
              min="1"
              class="item-row-number-field"
              @input="updateRow(index, 'itemRowNumber', parseInt($event) || 1)"
            ></v-text-field>
            <span v-else>{{ row.itemRowNumber }}</span>
          </td>
          <td class="pr-4 item-name-cell">
            <v-text-field
              v-if="isEditing"
              :value="row.itemName"
              dense
              hide-details
              class="item-name-field"
              @input="updateRow(index, 'itemName', $event)"
            ></v-text-field>
            <span v-else>{{ row.itemName }}</span>
          </td>
          <td class="pr-4">
            <v-select
              v-if="isEditing"
              :value="row.aiInvoiceAccountingValueTypeId"
              :items="accountingValueTypes"
              item-value="id"
              item-text="code"
              dense
              hide-details
              :loading="loadingAccountingValues"
              @input="
                updateRow(index, 'aiInvoiceAccountingValueTypeId', $event)
              "
            >
              <template v-slot:selection="{ item }">
                {{ item.code }} - {{ item.name }}
              </template>
              <template v-slot:item="{ item }">
                <span>{{ item.code }} - {{ item.name }}</span>
              </template>
            </v-select>
            <span v-else>
              {{ getAccountingValueTypeDisplay(row) }}
            </span>
          </td>
          <td class="pr-4">
            <v-select
              v-if="isEditing"
              :value="row.aiInvoiceAccountingValueId"
              :items="getFilteredAccountingValues(row)"
              item-value="id"
              item-text="code"
              dense
              hide-details
              :loading="loadingAccountingValues"
              :disabled="!row.aiInvoiceAccountingValueTypeId"
              @input="updateRow(index, 'aiInvoiceAccountingValueId', $event)"
            >
              <template v-slot:selection="{ item }">
                {{ item.code }} - {{ item.name }}
              </template>
              <template v-slot:item="{ item }">
                <span>{{ item.code }} - {{ item.name }}</span>
              </template>
            </v-select>
            <span v-else>
              {{ getAccountingValueDisplay(row) }}
            </span>
          </td>
          <td class="pr-4">
            <v-text-field
              v-if="isEditing"
              :value="row.itemQuantity"
              type="number"
              dense
              hide-details
              @input="updateRow(index, 'itemQuantity', parseFloat($event))"
            ></v-text-field>
            <span v-else>{{ row.itemQuantity }}</span>
          </td>
          <td class="pr-4">
            <v-text-field
              v-if="isEditing"
              :value="row.itemPrice"
              type="number"
              dense
              hide-details
              @input="updateRow(index, 'itemPrice', parseFloat($event))"
            ></v-text-field>
            <span v-else>{{ row.itemPrice }}</span>
          </td>
          <td class="pr-4">
            <v-text-field
              v-if="isEditing"
              :value="row.itemDiscountPercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              dense
              hide-details
              @input="
                updateRow(
                  index,
                  'itemDiscountPercentage',
                  parseFloat($event) || 0
                )
              "
            ></v-text-field>
            <span v-else>{{ row.itemDiscountPercentage || 0 }}%</span>
          </td>
          <td>
            <span>{{ row.itemTotalPrice }}</span>
          </td>
          <td v-if="isEditing">
            <v-btn
              icon
              x-small
              color="error"
              @click="$emit('delete-row', index)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
        <tr>
          <td :colspan="isEditing ? 7 : 6" class="text-right font-weight-bold">
            {{ $t("LABELS.TOTAL_SUM") }}:
          </td>
          <td class="font-weight-bold">
            {{ totalSum }}
          </td>
          <td v-if="isEditing"></td>
        </tr>
      </tbody>
    </table>
  </td>
</template>

<script>
export default {
  name: "InvoiceRowsEditor",
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    colspan: {
      type: Number,
      default: 12
    },
    accountingValueTypes: {
      type: Array,
      default: () => []
    },
    accountingValueOptions: {
      type: Array,
      default: () => []
    },
    loadingAccountingValues: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    totalSum() {
      if (!this.rows) return "0.00";
      return this.rows
        .reduce((sum, r) => sum + parseFloat(r.itemTotalPrice || 0), 0)
        .toFixed(2);
    }
  },
  methods: {
    updateRow(index, field, value) {
      this.$emit("update-row", index, field, value);
    },
    isErrorRow(row) {
      const totalPrice = parseFloat(row.itemTotalPrice || 0);
      const discountPercentage = parseFloat(row.itemDiscountPercentage || 0);
      const quantity = parseFloat(row.itemQuantity || 0);
      const price = parseFloat(row.itemPrice || 0);

      // Check if total price is 0 and discount is not 100%
      const zeroWithoutFullDiscount =
        totalPrice === 0 && discountPercentage !== 100;

      // Calculate expected total price with discount
      const subtotal = quantity * price;
      const discountAmount = subtotal * (discountPercentage / 100);
      const expectedTotal = subtotal - discountAmount;

      // Check if calculated total matches actual total (with small tolerance for floating point)
      const priceMismatch = Math.abs(expectedTotal - totalPrice) > 0.01;

      return zeroWithoutFullDiscount || priceMismatch;
    },
    getAccountingValueTypeDisplay(row) {
      if (row.accountingValue?.accountingValueType) {
        return `${row.accountingValue.accountingValueType.code} - ${row.accountingValue.accountingValueType.name}`;
      }

      if (row.accountingValueType) {
        return `${row.accountingValueType.code} - ${row.accountingValueType.name}`;
      }

      if (row.accountingValue?.aiInvoiceAccountingValueTypeId) {
        const type = this.accountingValueTypes.find(
          t => t.id === row.accountingValue.aiInvoiceAccountingValueTypeId
        );
        if (type) {
          return `${type.code} - ${type.name}`;
        }
      }

      if (row.aiInvoiceAccountingValueTypeId) {
        const type = this.accountingValueTypes.find(
          t => t.id === row.aiInvoiceAccountingValueTypeId
        );
        if (type) {
          return `${type.code} - ${type.name}`;
        }
      }

      return "-";
    },
    getAccountingValueDisplay(row) {
      if (row.accountingValue) {
        return `${row.accountingValue.code} - ${row.accountingValue.name}`;
      }
      return "-";
    },
    getFilteredAccountingValues(row) {
      if (!row.aiInvoiceAccountingValueTypeId) {
        return [];
      }

      const selectedType = this.accountingValueTypes.find(
        type => type.id === row.aiInvoiceAccountingValueTypeId
      );

      return selectedType?.values || [];
    }
  }
};
</script>

<style scoped>
.rows-table {
  width: 100%;
  border-collapse: collapse;
}

.rows-table th,
.rows-table td {
  text-align: left;
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.item-row-number-header {
  width: 40px;
}

.item-row-number-cell {
  width: 40px;
  text-align: center;
}

.item-name-header {
  width: 30%;
}

.item-name-cell {
  width: 30%;
}

.item-name-field {
  min-width: 200px;
  max-width: 100%;
}

.item-row-number-field {
  width: 60px;
  min-width: 40px;
  max-width: 100%;
}

.error-row {
  background-color: #ffebee !important;
}

.error-row td {
  color: #c62828;
}
</style>
